import { debounce, findIndex } from 'lodash';
import { RefCallback, useCallback, useEffect, useMemo, useRef, useState } from 'react';

type CallbackConfig = {
    node: HTMLElement;
    callback: (rect: ResizeObserverEntry['contentRect']) => void;
};

const globalCallbacks: CallbackConfig[] = [];

const globalObserver = new ResizeObserver((entries) => {
    entries.forEach((entry) => {
        globalCallbacks.forEach((config) => {
            if (config.node === entry.target) {
                config.callback(entry.contentRect);
            }
        });
    });
});

const removeCallback = (node: HTMLElement) => {
    const index = findIndex(globalCallbacks, (c) => c.node === node);
    globalCallbacks.splice(index, 1);
};

const addCallback = (node: HTMLElement, callback: CallbackConfig['callback']) => {
    globalCallbacks.push({
        node,
        callback
    });
    globalObserver.observe(node);
};

// debounceDelay = 16 cuts down the number of rerenders due to size change
// from ~10 per animation to about 3.
export const useElementBox = (debounceDelay = 16) => {
    const [contentRect, _setRect] = useState({ width: 0, height: 0 });
    const nodeRef = useRef<HTMLElement>();

    const setRect = useMemo(() => debounce(_setRect, debounceDelay), [debounceDelay]);

    const ref = useCallback<RefCallback<HTMLElement>>(
        (node) => {
            if (node) {
                setRect(node.getBoundingClientRect());
                nodeRef.current = node;
                addCallback(nodeRef.current, setRect);
            }
        },
        [setRect]
    );

    useEffect(() => {
        return () => {
            if (nodeRef.current) {
                globalObserver.unobserve(nodeRef.current);
                removeCallback(nodeRef.current);
                setRect.cancel();
            }
        };
    }, [setRect]);

    return [ref, contentRect] as const;
};
